import React, { useEffect, useRef, useState } from 'react';
import {
  Table,
  Tag,
  Button,
  Typography,
  Select,
  Modal,
  Descriptions,
  Alert,
} from 'antd';
import { CalendarOutlined, EyeOutlined, GoogleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';
import { apiService } from '../../../services/api';
import { jobPostingQueryKeys } from '../../../lib/queryClient';
import { InterviewResponse } from '../../../types';
import {
  formatInterviewDate,
  getInterviewStatusColor,
  getInterviewStatusText,
  getInterviewTypeText,
  formatGoogleCalendarDate,
} from '../../../utils/interviewDisplay';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const InterviewsTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedInterview, setSelectedInterview] = useState<InterviewResponse | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const openedFromUrlRef = useRef(false);

  const { data: interviews = [], isLoading: loading } = useQuery({
    queryKey: jobPostingQueryKeys.myInterviews,
    queryFn: async () => {
      const res = await apiService.getMyInterviews(1, 100);
      if (!res.success) throw new Error(res.message || 'Mülakatlar yüklenirken hata oluştu');
      return res.data?.data ?? [];
    },
  });

  const openDetail = (interview: InterviewResponse) => {
    setSelectedInterview(interview);
    setDetailModalVisible(true);
  };

  useEffect(() => {
    if (openedFromUrlRef.current || interviews.length === 0) return;
    const interviewIdParam = searchParams.get('interviewId');
    if (!interviewIdParam) return;

    const interviewId = Number(interviewIdParam);
    if (Number.isNaN(interviewId)) return;

    const interview = interviews.find((item) => item.id === interviewId);
    if (!interview) return;

    openedFromUrlRef.current = true;
    openDetail(interview);

    const next = new URLSearchParams(searchParams);
    next.delete('interviewId');
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [interviews, searchParams, setSearchParams]);

  const upcomingInterviews = interviews.filter(
    (item) =>
      item.status === 'Scheduled' &&
      dayjs(item.interviewDate).isAfter(dayjs().subtract(1, 'hour'))
  );

  const filteredInterviews = interviews.filter(
    (item) => !statusFilter || item.status === statusFilter
  );

  const addToGoogleCalendar = (interview: InterviewResponse) => {
    const startDate = dayjs(interview.interviewDate);
    const endDate = startDate.add(1, 'hour');
    const title = encodeURIComponent(`${interview.jobTitle} - Mülakat`);
    const details = encodeURIComponent(
      `Şirket: ${interview.companyName || '-'}\nTür: ${getInterviewTypeText(interview.interviewType)}`
    );
    const location = encodeURIComponent(interview.location || '');
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGoogleCalendarDate(startDate)}/${formatGoogleCalendarDate(endDate)}&details=${details}&location=${location}`;
    window.open(url, '_blank');
  };

  const columns = [
    {
      title: 'İş İlanı',
      key: 'job',
      render: (_: unknown, record: InterviewResponse) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.jobTitle || '-'}</div>
          <div style={{ color: '#666', fontSize: 12 }}>{record.companyName || '-'}</div>
        </div>
      ),
    },
    {
      title: 'Mülakat Tarihi',
      dataIndex: 'interviewDate',
      key: 'interviewDate',
      render: (date: string) => formatInterviewDate(date),
    },
    {
      title: 'Tür',
      dataIndex: 'interviewType',
      key: 'interviewType',
      render: (type: string) => getInterviewTypeText(type),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getInterviewStatusColor(status)}>{getInterviewStatusText(status)}</Tag>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: InterviewResponse) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
          Detay
        </Button>
      ),
    },
  ];

  return (
    <>
      <PanelTabCard
        title="Mülakatlarım"
        description="Planlanan ve geçmiş mülakatlarınızı buradan takip edebilirsiniz."
      >
        <TabListLoading loading={loading}>
          {upcomingInterviews.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${upcomingInterviews.length} yaklaşan mülakatınız var`}
              description={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {upcomingInterviews.slice(0, 3).map((item) => (
                    <div key={item.id}>
                      <Text strong>{item.jobTitle}</Text>
                      <Text> — {formatInterviewDate(item.interviewDate, true)}</Text>
                    </div>
                  ))}
                </div>
              }
            />
          )}

          <Select
            placeholder="Durum filtresi"
            value={statusFilter || undefined}
            onChange={setStatusFilter}
            style={{ width: 220, marginBottom: 16 }}
            allowClear
          >
            <Option value="Scheduled">Planlandı</Option>
            <Option value="Completed">Tamamlandı</Option>
            <Option value="Cancelled">İptal Edildi</Option>
            <Option value="Rescheduled">Yeniden Planlandı</Option>
          </Select>

          <Table
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={filteredInterviews}
            rowKey="id"
            loading={loading}
            locale={{ emptyText: 'Henüz mülakat kaydınız yok' }}
            pagination={{
              pageSize: 10,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} mülakat`,
            }}
          />
        </TabListLoading>
      </PanelTabCard>

      <Modal
        title="Mülakat Detayı"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Kapat
          </Button>,
        ]}
        width={720}
      >
        {selectedInterview && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="İş İlanı">{selectedInterview.jobTitle || '-'}</Descriptions.Item>
              <Descriptions.Item label="Şirket">{selectedInterview.companyName || '-'}</Descriptions.Item>
              <Descriptions.Item label="Mülakat Tarihi">
                {formatInterviewDate(selectedInterview.interviewDate, true)}
              </Descriptions.Item>
              <Descriptions.Item label="Tür">
                {getInterviewTypeText(selectedInterview.interviewType)}
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={getInterviewStatusColor(selectedInterview.status)}>
                  {getInterviewStatusText(selectedInterview.status)}
                </Tag>
              </Descriptions.Item>
              {selectedInterview.interviewerName && (
                <Descriptions.Item label="Mülakatçı">
                  {selectedInterview.interviewerName}
                </Descriptions.Item>
              )}
              {selectedInterview.location && (
                <Descriptions.Item label="Konum / Link">
                  <a href={selectedInterview.location} target="_blank" rel="noopener noreferrer">
                    {selectedInterview.location}
                  </a>
                </Descriptions.Item>
              )}
              {selectedInterview.notes && (
                <Descriptions.Item label="Notlar">
                  <div style={{ whiteSpace: 'pre-wrap' }}>{selectedInterview.notes}</div>
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedInterview.status === 'Scheduled' && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Takvime Ekle</Title>
                <Button icon={<GoogleOutlined />} onClick={() => addToGoogleCalendar(selectedInterview)}>
                  Google Takvim'e Ekle
                </Button>
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default InterviewsTab;
